/**
 * InComm card reporting — summary by card_status and partner product.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Badge } from '@/components/common/Badge';
import { Card, CardBody, CardHeader, StatCard } from '@/components/common/Card';
import type { Column } from '@/components/common/Table';
import { Table } from '@/components/common/Table';
import cardStyles from '@/components/common/Card/Card.module.scss';
import type {
  CardStatusCountItem,
  IncommCardsSummaryResponse,
  PartnerCardBreakdownItem,
  PartnerCardBreakdownResponse,
} from '@/app/services/incommCardService';
import {
  fetchIncommCardsPartnerBreakdown,
  fetchIncommCardsSummary,
} from '@/app/services/incommCardService';
import {
  cardStatusBadgeVariant,
  formatCardStatusLabel,
} from '@/pages/IncommCards/cardStatusBadgeVariant';
import { buildIncommListSearchString } from '@/pages/IncommCards/incommListNavigation';

import styles from './IncommCardsSummary.module.scss';

function formatMoney(amount: string): string {
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return amount;
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n);
}

/** Stable ordering: unset (null) first, then alphabetical by raw status. */
function sortCardStatusCounts(counts: CardStatusCountItem[]): CardStatusCountItem[] {
  return [...counts].sort((a, b) => {
    if (a.status == null && b.status != null) return -1;
    if (a.status != null && b.status == null) return 1;
    return (a.status ?? '').toLowerCase().localeCompare((b.status ?? '').toLowerCase());
  });
}

function statusDrillTestId(status: string | null): string {
  if (status == null) return 'incomm-drill-status-unset';
  return `incomm-drill-status-${status.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
}

/** HashRouter: `buildIncommListSearchString` returns `''` or `?a=b`. */
function listHref(queryFromBuilder: string): string {
  return `#/incomm-cards/list${queryFromBuilder}`;
}

function stopRowClick(e: React.MouseEvent): void {
  e.stopPropagation();
}

/** HashRouter listens on `hash`; avoids `useNavigate` so tests need no router mock. */
function goToIncommListQuery(queryFromBuilder: string): void {
  window.location.hash = `#/incomm-cards/list${queryFromBuilder}`;
}

export const IncommCardsSummary: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<IncommCardsSummaryResponse | null>(null);
  const [partnerBreakdown, setPartnerBreakdown] = useState<PartnerCardBreakdownResponse | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const [sum, partner] = await Promise.all([
          fetchIncommCardsSummary(),
          fetchIncommCardsPartnerBreakdown(),
        ]);
        if (!cancelled) {
          setSummary(sum);
          setPartnerBreakdown(partner);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          const message = e instanceof Error ? e.message : 'Unknown error';
          setError(message);
          setSummary(null);
          setPartnerBreakdown(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const navigateToCardStatusList = useCallback((row: CardStatusCountItem) => {
    const qs =
      row.status == null
        ? buildIncommListSearchString({ cardStatusUnset: true })
        : buildIncommListSearchString({ cardStatus: row.status });
    goToIncommListQuery(qs);
  }, []);

  const navigateToPartnerList = useCallback((row: PartnerCardBreakdownItem) => {
    if (row.partner_product_id == null) return;
    goToIncommListQuery(buildIncommListSearchString({ partnerProductId: row.partner_product_id }));
  }, []);

  const statusColumns = useMemo((): Column<CardStatusCountItem>[] => {
    return [
      {
        key: 'status',
        header: 'Card status',
        render: (row) => (
          <Badge variant={cardStatusBadgeVariant(row.status)} size="sm">
            {formatCardStatusLabel(row.status)}
          </Badge>
        ),
      },
      {
        key: 'count',
        header: 'Cardholders',
        align: 'right',
      },
      {
        key: 'drill',
        header: '',
        width: '11rem',
        render: (row) => (
          <a
            className={styles.drillLink}
            data-testid={statusDrillTestId(row.status)}
            href={listHref(
              row.status == null
                ? buildIncommListSearchString({ cardStatusUnset: true })
                : buildIncommListSearchString({ cardStatus: row.status })
            )}
            onClick={stopRowClick}
          >
            View cardholders →
          </a>
        ),
      },
    ];
  }, []);

  const partnerColumns = useMemo((): Column<PartnerCardBreakdownItem>[] => {
    return [
      {
        key: 'partner_name',
        header: 'Partner',
        render: (row) => row.partner_name ?? '—',
      },
      {
        key: 'doing_business_as',
        header: 'Doing business as',
        render: (row) => row.doing_business_as ?? '—',
      },
      {
        key: 'partner_product_id',
        header: 'Product ID',
        render: (row) => (row.partner_product_id != null ? String(row.partner_product_id) : '—'),
      },
      {
        key: 'totals',
        header: 'Totals',
        render: (row) => String(row.total_count),
      },
      {
        key: 'partner_drill',
        header: '',
        width: '10rem',
        render: (row) =>
          row.partner_product_id != null ? (
            <a
              className={styles.drillLink}
              data-testid={`incomm-drill-partner-all-${row.partner_product_id}`}
              href={listHref(
                buildIncommListSearchString({
                  partnerProductId: row.partner_product_id,
                })
              )}
              onClick={stopRowClick}
            >
              All in partner →
            </a>
          ) : (
            '—'
          ),
      },
      {
        key: 'counts',
        header: 'Per status',
        render: (row) => (
          <ul className={styles.nestedCounts}>
            {row.counts.map((c) => (
              <li key={`${String(c.status)}-${c.count}`}>
                <Badge variant={cardStatusBadgeVariant(c.status)} size="sm">
                  {formatCardStatusLabel(c.status)}
                </Badge>{' '}
                {c.count}
                {row.partner_product_id != null && (
                  <>
                    {' '}
                    <a
                      className={styles.drillLink}
                      data-testid={`incomm-drill-partner-${row.partner_product_id}-status-${
                        c.status == null ? 'unset' : c.status.replace(/[^a-z0-9]+/gi, '-').toLowerCase()
                      }`}
                      href={listHref(
                        c.status == null
                          ? buildIncommListSearchString({
                              cardStatusUnset: true,
                              partnerProductId: row.partner_product_id,
                            })
                          : buildIncommListSearchString({
                              cardStatus: c.status,
                              partnerProductId: row.partner_product_id,
                            })
                      )}
                      onClick={stopRowClick}
                    >
                      View →
                    </a>
                  </>
                )}
              </li>
            ))}
          </ul>
        ),
      },
    ];
  }, []);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>InComm cards</h1>
          <p className={styles.pageDescription}>
            Cardholders (incomm_demo), purses, and reward funding — totals and breakdown by partner
            product (udf_1 → partner_configurations).
          </p>
        </div>
        <a className={styles.toolbarLink} href="#/incomm-cards/list">
          View all cardholders
        </a>
      </header>

      {loading && <div className={styles.loading}>Loading InComm card reports…</div>}

      {error != null && (
        <div className={styles.errorMessage} role="alert">
          Failed to load InComm card reports: {error}
        </div>
      )}

      {!loading && error == null && summary != null && (
        <>
          <div className={styles.statsGrid}>
            <div
              data-testid="incomm-total-cardholders"
              className={`${styles.statWrap} ${styles.clickableStatWrap}`}
              role="button"
              tabIndex={0}
              aria-label="Open full cardholder list"
              onClick={() => goToIncommListQuery('')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goToIncommListQuery('');
                }
              }}
            >
              <StatCard label="Total cardholders" value={summary.total_cardholders} />
            </div>
            <div data-testid="incomm-total-purses" className={styles.statWrap}>
              <StatCard label="Total purses" value={summary.total_purses} />
            </div>
            <div className={cardStyles.statCard} data-testid="incomm-total-purse-balance">
              <div className={cardStyles.statLabel}>Total purse balance</div>
              <div className={cardStyles.statValue}>{formatMoney(summary.total_purse_balance)}</div>
            </div>
            <div data-testid="incomm-total-reward-rows" className={styles.statWrap}>
              <StatCard label="Reward rows" value={summary.total_reward_rows} />
            </div>
            <div className={cardStyles.statCard} data-testid="incomm-total-reward-amount">
              <div className={cardStyles.statLabel}>Total reward amount</div>
              <div className={cardStyles.statValue}>{formatMoney(summary.total_reward_amount)}</div>
            </div>
          </div>

          <div className={styles.sectionStack}>
            <Card>
              <CardHeader
                title="By card status"
                subtitle="Tap a row (or a link) to open the cardholder list with that card_status filter applied."
              />
              <CardBody>
                {summary.card_status_counts.length === 0 ? (
                  <p className={styles.pageDescription}>No cardholders in scope.</p>
                ) : (
                  <div data-testid="incomm-by-status-table">
                    <Table
                      columns={statusColumns}
                      data={sortCardStatusCounts(summary.card_status_counts)}
                      keyExtractor={(row) => (row.status == null ? 'null-status' : row.status)}
                      emptyMessage="No status rows"
                      onRowClick={navigateToCardStatusList}
                    />
                  </div>
                )}
              </CardBody>
            </Card>

            {partnerBreakdown != null && partnerBreakdown.items.length > 0 && (
              <Card>
                <CardHeader
                  title="By partner product"
                  subtitle="Tap a partner row for all cardholders in that partner; use per-status links for a narrower list."
                />
                <CardBody>
                  <div data-testid="incomm-partner-breakdown">
                    <Table
                      columns={partnerColumns}
                      data={partnerBreakdown.items}
                      keyExtractor={(row) =>
                        `${String(row.partner_product_id ?? 'null')}-${row.partner_name ?? ''}-${row.doing_business_as ?? ''}`
                      }
                      emptyMessage="No partner rows"
                      onRowClick={navigateToPartnerList}
                    />
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default IncommCardsSummary;
